import { AssistantPageState, AssistantSelection } from '../assistantContextTypes';
import { getPageDescriptor } from '../assistantPageRegistry';

/**
 * Forecast V3 → assistant page state.
 *
 * Publishes ONLY what the user can currently see or has explicitly selected:
 * scope, mode, version, year, active month, the department filter, and the
 * selected row. No account values, no calculated totals, no API responses —
 * the server fetches every figure itself through report collectors.
 *
 * A pure function so the page's own React context stays private to the page
 * and this mapping can be tested without rendering anything.
 */

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export interface ForecastV3ContextInput {
  orgId: number | null | undefined;
  locationId: number | null | undefined;
  locationName?: string | null;
  versionId?: number | string | null;
  versionName?: string | null;
  versionType?: string | null;
  year?: number | null;
  activeMonth?: number | null;
  departmentId?: number | null;
  selection?: AssistantSelection | null;
  route?: string | null;
}

function toInt(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : parseInt(String(value), 10);
  return Number.isInteger(n) ? n : null;
}

export function buildForecastV3Context(input: ForecastV3ContextInput): AssistantPageState | null {
  const orgId = toInt(input.orgId);
  const locationId = toInt(input.locationId);
  if (orgId == null || locationId == null) return null;

  const isBudget = String(input.versionType || '').toLowerCase() === 'budget';
  const versionId = toInt(input.versionId);
  const year = toInt(input.year);
  const activeMonth = toInt(input.activeMonth);
  const departmentId = toInt(input.departmentId);

  const descriptor = getPageDescriptor('forecast_v3');
  // Advisory only — the server decides what this context actually grants, and
  // in particular refuses the forecast-only accuracy review on a budget.
  // Month-end landing is read-only, so a budget page keeps it — the server
  // treats the selected budget as a benchmark. The accuracy review is
  // forecast-only and is withheld here as well as refused server-side.
  const candidateCapabilities = (descriptor?.candidateCapabilities || [])
    .filter(cap => (isBudget ? cap !== 'forecast.review_accuracy' : true));

  const labelParts: (string | null)[] = [input.locationName || null];
  if (input.versionName) {
    labelParts.push(year && !input.versionName.includes(String(year)) ? `${year} ${input.versionName}` : input.versionName);
  }
  if (activeMonth && activeMonth >= 1 && activeMonth <= 12) labelParts.push(MONTH_NAMES[activeMonth - 1]);
  if (input.selection?.label) labelParts.push(input.selection.label);

  return {
    pageKey: 'forecast_v3',
    orgId,
    propertyIds: [locationId],
    view: {
      route: input.route || '/forecast-v3',
      mode: isBudget ? 'budget' : 'forecast',
      versionId,
      year,
      activeMonth,
    },
    filters: departmentId != null ? { departmentId } : {},
    selection: input.selection ?? null,
    dataRefs: versionId != null
      ? [{ kind: isBudget ? 'budget_version' : 'forecast_version', id: versionId }]
      : [],
    capabilities: candidateCapabilities,
    labelParts,
  };
}

export default buildForecastV3Context;
